import { useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Save } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { legToSnapshot, parseReferenceDateKey, toReferenceDateKey } from '@/utils/savedMultiples';
import { toast } from 'sonner';

export function commitGestaoSnapshot({
  legs,
  stake,
  combinedOdd,
  grossReturn,
  estimatedProfit,
  addSnapshot,
  referenceDate,
  saveTitle,
  onSuccess,
}) {
  if (!legs?.length) {
    toast.error('Adicione pelo menos uma seleção.');
    return false;
  }
  const ref = toReferenceDateKey(referenceDate);
  const title =
    (saveTitle && String(saveTitle).trim()) ||
    `Múltipla ${format(referenceDate, 'dd/MM/yyyy', { locale: ptBR })}`;
  addSnapshot({
    referenceDate: ref,
    title,
    stake,
    combinedOdd,
    grossReturn,
    estimatedProfit,
    legsCount: legs.length,
    legs: legs.map(legToSnapshot),
  });
  onSuccess?.();
  toast.success('Registro salvo com a seleção e o cálculo atual.');
  return true;
}

export default function GestaoSaveSnapshotSection({
  legs,
  stake,
  combinedOdd,
  grossReturn,
  estimatedProfit,
  addSnapshot,
  snapshots,
  referenceDate,
  onReferenceDateChange,
  saveTitle,
  onSaveTitleChange,
}) {
  const datesWithRecords = useMemo(() => {
    const set = new Set(snapshots.map((s) => s.referenceDate));
    return Array.from(set)
      .map(parseReferenceDateKey)
      .filter(Boolean);
  }, [snapshots]);

  const canSave = legs.length > 0;

  const handleSave = () => {
    commitGestaoSnapshot({
      legs,
      stake,
      combinedOdd,
      grossReturn,
      estimatedProfit,
      addSnapshot,
      referenceDate,
      saveTitle,
      onSuccess: () => onSaveTitleChange(''),
    });
  };

  return (
    <div className="rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50/50 to-white/80 p-4 dark:border-emerald-900/30 dark:from-emerald-950/20 dark:to-neutral-950/40">
      <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
        <Save className="h-3.5 w-3.5" />
        Salvar no histórico
      </p>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex-1 space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-500">
            Título (opcional)
          </label>
          <Input
            placeholder="Ex.: Bilhete fim de semana"
            value={saveTitle}
            onChange={(e) => onSaveTitleChange(e.target.value)}
            className="h-10 rounded-xl border-slate-200/90 bg-white dark:border-white/[0.08] dark:bg-neutral-900/60"
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-500">
              Data de referência
            </span>
            <p className="text-xs text-slate-600 dark:text-neutral-400">
              {format(referenceDate, "EEEE, d 'de' MMMM yyyy", { locale: ptBR })}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-10 shrink-0 rounded-xl bg-emerald-600 px-5 font-semibold text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="mr-2 h-4 w-4" />
            Salvar
          </Button>
        </div>
      </div>
      <div className="mt-4 flex justify-center sm:justify-start">
        <Calendar
          mode="single"
          selected={referenceDate}
          onSelect={(d) => d && onReferenceDateChange(d)}
          locale={ptBR}
          weekStartsOn={0}
          modifiers={{ temRegisto: datesWithRecords }}
          modifiersClassNames={{
            temRegisto:
              'relative after:absolute after:bottom-0.5 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-emerald-500',
          }}
          className="rounded-2xl border border-slate-200/60 bg-white p-2 dark:border-white/[0.08] dark:bg-neutral-950/50"
        />
      </div>
      <p className="mt-2 text-[11px] text-slate-500 dark:text-neutral-500">
        O registro inclui as seleções atuais, stake e valores calculados. Dias com ponto verde já
        têm registros nessa data.
      </p>
    </div>
  );
}
